/**
 * <klapi-header> : mega menus desktop et tiroir mobile.
 *
 * - Un bouton [data-panneau="cle"] ouvre le panneau [data-panneau-contenu="cle"].
 * - Desktop (pointeur fin) : ouverture au survol (150 ms, 200 ms en sortie), au clic et au clavier.
 * - Échap (le focus revient sur le déclencheur), clic extérieur, survol d'un autre lien
 *   ou sortie du focus hors de l'élément de menu ferment le panneau. Un seul panneau ouvert.
 * - Tiroir mobile : [data-tiroir-ouvrir] / [data-tiroir-fermer], scroll bloqué via html[scroll-lock] (Horizon),
 *   focus piégé dans le tiroir, Échap ferme.
 */
// Le script peut être inclus par plusieurs sections : l'élément n'est déclaré qu'une fois
if (!customElements.get('klapi-header')) {
  class KlapiHeader extends HTMLElement {
    connectedCallback() {
      this.boutons = [...this.querySelectorAll('[data-panneau]')];
      this.panneaux = [...this.querySelectorAll('[data-panneau-contenu]')];
      this.tiroir = this.querySelector('.k-tiroir');
      this.boutonTiroir = this.querySelector('[data-tiroir-ouvrir]');
      this.survol = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.ouvert = null;
      this.minuteur = null;

      this.boutons.forEach((bouton) => {
        bouton.addEventListener('click', () => this.basculer(bouton.dataset.panneau));
        const item = bouton.closest('.k-header__item') || bouton;
        item.addEventListener('pointerenter', () => this.survolEntree(bouton.dataset.panneau));
        item.addEventListener('pointerleave', () => this.survolSortie());
      });

      // Survol d'un lien simple de premier niveau : ferme le panneau ouvert
      this.querySelectorAll('.k-header__lien:not([data-panneau])').forEach((lien) => {
        lien.addEventListener('pointerenter', () => {
          if (!this.survol.matches || !this.ouvert) return;
          clearTimeout(this.minuteur);
          this.fermer();
        });
      });

      this.surClavier = this.surClavier.bind(this);
      this.surClicExterieur = this.surClicExterieur.bind(this);
      document.addEventListener('keydown', this.surClavier);
      document.addEventListener('click', this.surClicExterieur);
      // Chaque panneau suit son bouton dans le DOM : il se ferme quand le focus quitte cet élément de menu
      this.addEventListener('focusout', (event) => {
        if (!this.ouvert) return;
        const item = this.boutons.find((b) => b.dataset.panneau === this.ouvert)?.closest('.k-header__item');
        if (item && !item.contains(event.relatedTarget)) this.fermer();
      });

      // Compactage au scroll (Horizon fait défiler .page-wrapper : écoute en capture)
      this.surScroll = this.surScroll.bind(this);
      document.addEventListener('scroll', this.surScroll, { passive: true, capture: true });
      this.surScroll();

      this.boutonTiroir?.addEventListener('click', () => this.ouvrirTiroir());
      this.querySelector('[data-tiroir-fermer]')?.addEventListener('click', () => this.fermerTiroir());
    }

    disconnectedCallback() {
      document.removeEventListener('scroll', this.surScroll, { capture: true });
      document.removeEventListener('keydown', this.surClavier);
      document.removeEventListener('click', this.surClicExterieur);
      document.documentElement.removeAttribute('scroll-lock');
    }

    surScroll(event) {
      const cible = event?.target;
      const y = cible && cible !== document && cible.scrollTop !== undefined ? cible.scrollTop : window.scrollY;
      if (cible && cible !== document && !cible.classList?.contains('page-wrapper')) return;
      this.toggleAttribute('data-compact', y > 80);
    }

    survolEntree(cle) {
      if (!this.survol.matches) return;
      clearTimeout(this.minuteur);
      this.minuteur = setTimeout(() => this.ouvrir(cle), this.ouvert ? 0 : 150);
    }

    survolSortie() {
      if (!this.survol.matches) return;
      clearTimeout(this.minuteur);
      this.minuteur = setTimeout(() => this.fermer(), 200);
    }

    basculer(cle) {
      clearTimeout(this.minuteur);
      // Un clic juste après une ouverture au survol ne doit pas refermer le panneau
      if (this.ouvert === cle && Date.now() - this.ouvertA < 400) return;
      if (this.ouvert === cle) this.fermer();
      else this.ouvrir(cle);
    }

    ouvrir(cle) {
      if (this.ouvert !== cle) this.ouvertA = Date.now();
      this.ouvert = cle;
      this.boutons.forEach((b) => b.setAttribute('aria-expanded', String(b.dataset.panneau === cle)));
      this.panneaux.forEach((p) => {
        p.hidden = p.dataset.panneauContenu !== cle;
      });
    }

    fermer() {
      this.ouvert = null;
      this.boutons.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this.panneaux.forEach((p) => {
        p.hidden = true;
      });
    }

    surClicExterieur(event) {
      if (this.ouvert && !this.contains(event.target)) this.fermer();
    }

    surClavier(event) {
      if (event.key === 'Escape') {
        if (this.tiroir && !this.tiroir.hidden) {
          this.fermerTiroir();
          return;
        }
        if (this.ouvert) {
          const bouton = this.boutons.find((b) => b.dataset.panneau === this.ouvert);
          this.fermer();
          bouton?.focus();
        }
        return;
      }

      if (event.key === 'Tab' && this.tiroir && !this.tiroir.hidden) this.pieger(event);
    }

    ouvrirTiroir() {
      if (!this.tiroir) return;
      this.tiroir.hidden = false;
      this.boutonTiroir.setAttribute('aria-expanded', 'true');
      document.documentElement.setAttribute('scroll-lock', '');
      this.tiroir.querySelector('[data-tiroir-fermer]')?.focus();
    }

    fermerTiroir() {
      if (!this.tiroir) return;
      this.tiroir.hidden = true;
      this.boutonTiroir.setAttribute('aria-expanded', 'false');
      document.documentElement.removeAttribute('scroll-lock');
      this.boutonTiroir.focus();
    }

    pieger(event) {
      const focusables = [
        ...this.tiroir.querySelectorAll('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'),
      ].filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const premier = focusables[0];
      const dernier = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === premier) {
        event.preventDefault();
        dernier.focus();
      } else if (!event.shiftKey && document.activeElement === dernier) {
        event.preventDefault();
        premier.focus();
      }
    }
  }

  customElements.define('klapi-header', KlapiHeader);
}
